import { useCallback } from 'react';

interface RefetchResult {
  data?: { downloadUrl?: string | null } | undefined;
}

interface UsePresignedDocumentDownloadParams {
  /** Currently cached presigned URL, if any. */
  downloadUrl: string | undefined;
  /** ISO expiry of the cached URL. */
  expiresAt: string | undefined;
  /** Filename used for the `download` attribute. */
  filename: string | undefined;
  /** Refetches a fresh presigned URL (e.g. a react-query refetch). */
  refetchDownloadUrl: () => Promise<RefetchResult>;
}

/**
 * Shared download/open behaviour for presigned (S3) document URLs.
 *
 * Presigned URLs expire, so we cannot rely on a static <a href>: every action
 * checks expiry and refetches a fresh URL before navigating. Used by both the
 * lab documents tab and the Walmart insights banner.
 */
export const usePresignedDocumentDownload = ({
  downloadUrl,
  expiresAt,
  filename,
  refetchDownloadUrl,
}: UsePresignedDocumentDownloadParams) => {
  const getOrRefreshUrl = useCallback(async () => {
    if (downloadUrl && expiresAt && new Date(expiresAt) > new Date()) {
      return downloadUrl;
    }
    const result = await refetchDownloadUrl();
    return result.data?.downloadUrl ?? undefined;
  }, [downloadUrl, expiresAt, refetchDownloadUrl]);

  const downloadDocument = useCallback(async () => {
    const url = await getOrRefreshUrl();
    if (!url) {
      return;
    }
    try {
      // Fetch as a blob so the `download` attribute is respected — browsers
      // ignore it on cross-origin URLs (like S3 presigned URLs).
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename ?? '';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Fallback: open in a new tab to avoid clobbering the current session.
      window.open(url, '_blank');
    }
  }, [getOrRefreshUrl, filename]);

  const openInNewTab = useCallback(async () => {
    const url = await getOrRefreshUrl();
    if (!url) {
      return;
    }
    window.open(url, '_blank');
  }, [getOrRefreshUrl]);

  return { downloadDocument, openInNewTab };
};
